'use client';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { createContext, useContext, ReactNode, useState, useEffect } from 'react';

import { useLogger } from '@/analytics/useLogger';
import { Experiment, LDFlagKeys, toExperimentVariation } from '@/consts/LDFlags';

const ExperimentsContext = createContext<Experiment[]>([]);
export const useLDExperiments = () => useContext(ExperimentsContext);

type Props = { children: ReactNode; initialValue: Experiment[] };

export const ExperimentsProvider = ({ children, initialValue }: Props) => {
  const ldClient = useLDClient();
  const [experiments, setExperiments] = useState(initialValue);
  const { addError } = useLogger();

  useEffect(() => {
    if (!ldClient) {
      return;
    }

    let cancelled = false;

    ldClient
      .waitForInitialization(5)
      .then(() => {
        if (cancelled) {
          return;
        }

        const allFlagKeys = Object.keys(ldClient.allFlags()) as LDFlagKeys[];
        const ldExperiments = allFlagKeys
          .filter((key) => !key.startsWith('$'))
          .map((key) => ({ detail: ldClient.variationDetail(key), key }))
          .filter(({ detail }) => detail.reason?.inExperiment)
          .map(({ detail, key }) => ({
            name: key,
            variation: toExperimentVariation(detail.value, detail.variationIndex),
          }));

        setExperiments(ldExperiments);
      })
      .catch((err) => addError(err, { component: 'LaunchDarklyProvider' }));

    return () => {
      cancelled = true;
    };
  }, [addError, ldClient]);

  return <ExperimentsContext value={experiments}>{children}</ExperimentsContext>;
};
